import * as React from "react";
import { useContext, useState, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import { getCareScore } from "../../common/Formatters";
import {
	ModuleType,
} from "src/common/Enums";
import { DataTable, Spinner, ModalOverlay } from "src/components/common";
import { Button, IconButton } from "src/components/buttons";
import { Icon } from "src/components/icons/Icon";
import { Lock, FileSpreadsheet, ClipboardCheck, SquarePen } from "lucide-react";
import { Popover, Tooltip } from "antd";
import ClientResultModalOverlay from "src/components/forms/ClientResultModalOverlay";

import AssessmentResultsAPI from "src/admin/results/AssessmentResultsAPI";
import ClientAPI from "../../clients/ClientAPI";
import LocationAPI from "src/admin/Organization/Location/LocationAPI";
import DatasetAPI from "src/admin/Organization/Datasets/DatasetAPI";
import ClientDatasetAPI from "src/clientsiteresults/ClientDatasetAPI";
import SwitchFilter from "src/components/common/SwitchFilter";
import NeurapulseFilter from "../common/NeurapulseFilter";
import CoreAPI from "src/core/CoreAPI";

import PickListContext from "src/context/pickListContext";

import CustomFieldAPI from "src/admin/Organization/CustomFields/CustomFieldAPI";
import { CustomFields } from "src/common/CustomFields";

import Utils, { getTime } from "src/common/utils";
import {
	setDateColumnCellValue,
	DateFilter,
	dateFilterMethod,
} from "src/components/forms/DateFilter";
import { AdminPermissions, ClientPermissions } from "src/common/Permissions";
import OrganizationAPI from "src/admin/Organization/API/OrganizationAPI";
import {
	getCustomFieldValues,
	getCustomFieldValidation,
	ICustomFieldValidation,
} from "src/admin/Organization/CustomFields/CustomFieldContainer";
import ReportAPI from "src/admin/report/ReportAPI";
import GenerateExceleRATEPdf from "src/admin/report/templates/v1/GenerateExceleRATEPdf";
import CustomProtocolHandler from "src/vitals/CustomProtocolHandler";
import Dropdown from "react-bootstrap/Dropdown";
import { CSVLink } from "react-csv";
import {
	ClientResultsHeaders,
} from "src/common/CsvExport";
import ClientAppConstants from "src/common/Constants";
import {
	hasAnyExceleRATEProduct,
	hasExceleRATEV1OnlyProduct,
	hasDriveABLEProduct,
	hasAnyProduct,
} from "src/components/forms/ProductType";
import {
	inofficeRecommendationToolTip,
	inOfficeScoreToolTip,
	onroadScoreToolTip,
	overallScoreToolTip,
} from "./TestToolTip";
import {
	IModule,
	ModulesClient,
	Assessment,
	DatasetPurposeType,
	DatasetsClient,
} from "src/@types/ImpiricaPlatformApi";
import { toast } from "react-toastify";
import NeurapulseIcon, { getNeurapulseTitle } from "../common/NeurapulseIcon";

// Shared hooks
import { useResultsFilters } from "src/components/assessmentResults/hooks/useResultsFilters";
import { useVitalsHandler } from "src/components/assessmentResults/hooks/useVitalsHandler";
import { useReportGeneration } from "src/components/assessmentResults/hooks/useReportGeneration";

// Cell components
import { VitalsScoreCell } from "src/components/assessmentResults/cells/VitalsScoreCell";
import { VitalsRecommendationCell } from "src/components/assessmentResults/cells/VitalsRecommendationCell";
import { InofficeRecommendationCell } from "src/components/assessmentResults/cells/InofficeRecommendationCell";
import { CoreRecommendationCell } from "src/components/assessmentResults/cells/CoreRecommendationCell";
import { DoreRecommendationCell } from "src/components/assessmentResults/cells/DoreRecommendationCell";
import { NeurapulseResultCell } from "src/components/assessmentResults/cells/NeurapulseResultCell";

// Utils
import {
	getCsvData,
	getDatasetPermissions,
	canSeeScores,
	returnedAssessmentCustomFields,
	setMultipleLabelComponent,
	IGetCsvDataResult,
} from "src/components/assessmentResults/assessmentResultsUtils";

interface IProps {
	history: any;
	location: any;
	clientId: number;
	organizationId: number;
	datasetId: number;
	api?: any;
	currentUser: any;
}

const clientResultsGridStorageKey = "ClientResultsGridStorageKey_Id_";

const ClientResultsTable: React.FC<IProps> = ({
	history,
	location,
	clientId,
	organizationId,
	datasetId,
	api,
	currentUser,
}) => {
	const context = useContext(PickListContext);

	// ─── Core data state ────────────────────────────────────────────────────────
	const [results, setResults] = useState<ClientSiteAssessmentResultsDTO[]>([]);
	const [dataset, setDataset] = useState<DatasetDTO>({} as DatasetDTO);
	const [client, setClient] = useState<ClientDTO>({} as ClientDTO);
	const [locations, setLocations] = useState<any[]>([]);
	const [datasetLanguages, setDatasetLanguages] = useState<PickListItemResponseDTO[]>([]);
	const [customFields, setCustomFields] = useState<CustomFieldDTO[]>([]);
	const [datasetModules, setDatasetModules] = useState<IModule[]>([]);
	const [commercialDrivingExpSettings, setCommercialDrivingExpSettings] = useState<CommercialDrivingExpSettingsDTO>({} as CommercialDrivingExpSettingsDTO);

	// ─── UI state ───────────────────────────────────────────────────────────────
	const [isLoading, setIsLoading] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isNewTestConfirmationOpen, setIsNewTestConfirmationOpen] = useState(false);
	const [selectedAssessmentResult, setSelectedAssessmentResult] = useState<ClientSiteAssessmentResultsDTO | undefined>(undefined);
	const [viewResults, setViewResults] = useState(false);
	const [scoring, setScoring] = useState(false);
	const [isCoreCertified, setIsCoreCertified] = useState(false);

	// ─── Modal / test state ──────────────────────────────────────────────────────
	const [newTestCustomFieldValues, setNewTestCustomFieldValues] = useState<CustomFieldValueDTO[]>([]);
	const [newTestPurposeId, setNewTestPurposeId] = useState<number | undefined>(undefined);
	const [validNewTestValidation, setValidNewTestValidation] = useState<string[]>([]);

	// ─── CSV state ───────────────────────────────────────────────────────────────
	const [csvTestResults, setCsvTestResults] = useState<IGetCsvDataResult>({ headers: [], csvResults: [] });
	const [csvTestResultsFiltered, setCsvTestResultsFiltered] = useState<IGetCsvDataResult>({ headers: [], csvResults: [] });

	// ─── Permissions ─────────────────────────────────────────────────────────────
	const datasetPermissions = getDatasetPermissions(currentUser, datasetId);
	const userCanSeeScores = canSeeScores(currentUser, datasetPermissions);

	// ─── Filter hook ─────────────────────────────────────────────────────────────
	const {
		filterState,
		filtered,
		setFiltered,
		setFilterValue,
		setNeurapulseFilterValue,
		setFilterMethodForSwitch,
		setFilterMethodForNeurapulseSwitch,
		showOrHideDisplayFilter,
		closeFilter,
		clearFilters,
		onDateChange,
	} = useResultsFilters(userCanSeeScores);

	// ─── onPollComplete — uses ref to break circularity with vitals hook ─────────
	const onPollCompleteRef = useRef<(vitalsRunning: boolean) => void>(() => {});

	// ─── Vitals hook ─────────────────────────────────────────────────────────────
	const {
		vitalsLaunched,
		DCATButtonClicked,
		setDCATButtonClicked,
		DCATRunning,
		setDCATRunning,
		DCATerror,
		setDCATerror,
		canSync,
		setCanSync,
		syncSrc,
		setSyncSrc,
		showLaunchMessageVisible,
		vitalsTestResultId,
		setVitalsTestResultId,
		options,
		setVitalsLaunched,
		handleCustomProtocolError,
		startVitalsTest: startVitalsTestBase,
		resumeVitalsTest: resumeVitalsTestBase,
		initCanSync,
	} = useVitalsHandler({ currentUser, onPollComplete: (v) => onPollCompleteRef.current(v) });

	// Wire up onPollComplete after hook is called so vitalsTestResultId is available
	onPollCompleteRef.current = useCallback(
		(vitalsRunning: boolean) => {
			if (!vitalsRunning && selectedAssessmentResult && vitalsTestResultId) {
				history.push(
					`/organizations/${organizationId}/datasets/${datasetId}/clients/${clientId}/sessionId/${selectedAssessmentResult.id}/vitalsTestResults/${vitalsTestResultId}`
				);
			}
		},
		[history, organizationId, datasetId, clientId, selectedAssessmentResult, vitalsTestResultId]
	);

	// ─── Report generation hook ───────────────────────────────────────────────────
	const { fetchReportData, isGeneratingPdf } = useReportGeneration();

	// ─── React table ref ──────────────────────────────────────────────────────────
	const reactTable = useRef<DataTable>(null);

	// ─── Data loading ─────────────────────────────────────────────────────────────
	const getClientDatasetModules = useCallback(async (): Promise<IModule[]> => {
		const modulesClient: ModulesClient = api.modulesClient;
		return modulesClient.getAllModulesForDatasetPermissions(
			organizationId,
			datasetId,
			currentUser.id
		);
	}, [api, organizationId, datasetId, currentUser]);

	const loadData = useCallback(async () => {
		const dp = getDatasetPermissions(currentUser, datasetId);
		const modules = await getClientDatasetModules();

		if (
			currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
			(dp &&
				dp.permissions &&
				(dp.permissions.includes(ClientPermissions.ViewAllResults) ||
					dp.permissions.includes(ClientPermissions.ViewOwnResults)))
		) {
			const p_client = ClientAPI.getClientSiteClient({ datasetId, clientId });
			const p_location = LocationAPI.getClientSiteDatasetLocations({ organizationId, datasetId });
			const p_datasetLanguages = DatasetAPI.getDatasetLanguages({ organizationId, datasetId });
			const p_assessmentResults = AssessmentResultsAPI.getClientSiteAssessmentResults({ datasetId, clientId });
			const p_orgCustFields = CustomFieldAPI.getOrganizationCustomFields(organizationId, datasetId);
			const p_dataset = ClientDatasetAPI.getDatset({ organizationId, datasetId });
			const p_commercialDrivingExpSettings = await OrganizationAPI.getCommercialDrivingExpSettings({
				tenantId: organizationId,
				datasetId,
			});

			Promise.all([
				p_client,
				p_location,
				p_datasetLanguages,
				p_assessmentResults,
				p_orgCustFields,
				p_dataset,
				p_commercialDrivingExpSettings,
			])
				.then((values) => {
					const [
						clientData,
						locationData,
						datasetLanguagesData,
						assessmentResults,
						orgCustFields,
						datasetData,
						commercialDrivingExpSettingsData,
					] = values;

					initCanSync(currentUser);

					let _isCoreCertified = false;
					if (
						currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
						currentUser.coreCertified
					) {
						_isCoreCertified = true;
					}
					setIsCoreCertified(_isCoreCertified);

					const filteredCustomFields = returnedAssessmentCustomFields(
						orgCustFields,
						context.pickListItems,
						datasetId
					);

					setLocations(locationData);
					setDatasetLanguages(datasetLanguagesData);
					setClient(clientData);
					setResults(assessmentResults);
					setCustomFields(filteredCustomFields);
					setDataset(datasetData);
					setViewResults(true);
					setDatasetModules(modules);
					setCommercialDrivingExpSettings(commercialDrivingExpSettingsData);

					const csvData = getCsvData(assessmentResults, ClientResultsHeaders, datasetData, false);
					setCsvTestResults(csvData);
					setCsvTestResultsFiltered(csvData);
				})
				.finally(() => setIsLoading(false));
		} else {
			setIsLoading(false);
		}
	}, [
		clientId,
		organizationId,
		datasetId,
		currentUser,
		context.pickListItems,
		getClientDatasetModules,
		setCanSync,
		initCanSync,
	]);

	useEffect(() => {
		loadData();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// ─── Helpers ──────────────────────────────────────────────────────────────────
	const onDataFiltered = useCallback(
		(data: any) => {
			const filteredData = getCsvData(data as ClientSiteAssessmentResultsDTO[], ClientResultsHeaders, dataset, false);
			setCsvTestResultsFiltered(filteredData);
		},
		[dataset]
	);

	const convertDisplayOnValuesToLabelComponent = useCallback((row: any) => {
		const labelValue =
			row.value !== undefined && row.value !== "" ? row.value.toString() : null;
		if (labelValue !== null) {
			const displayValues = labelValue.split(",").join(", ");
			return setMultipleLabelComponent([displayValues]);
		}
		return "";
	}, []);

	const clearAll = useCallback(() => {
		clearFilters();
		const csvData = getCsvData(results, ClientResultsHeaders, dataset, false);
		setCsvTestResults(csvData);
		setCsvTestResultsFiltered(csvData);
	}, [clearFilters, results, dataset]);

	// ─── Assessment helpers ────────────────────────────────────────────────────────
	const enableEditAssessment = useCallback(
		(assessmentResult: ClientSiteAssessmentResultsDTO): boolean => {
			const dp = getDatasetPermissions(currentUser, datasetId);
			if (assessmentResult.dataLocked) return false;
			if (
				dp &&
				dp.permissions &&
				!dp.permissions.includes(ClientPermissions.EditCoreTest) &&
				!dp.permissions.includes(ClientPermissions.EditVitalsTest) &&
				!currentUser.permissions!.includes(AdminPermissions.GlobalAdmin)
			) {
				return false;
			}
			return hasAnyProduct(dataset.productId);
		},
		[currentUser, datasetId, dataset]
	);

	const enableStartAssessment = useCallback(
		(): boolean => hasAnyProduct(dataset.productId),
		[dataset]
	);

	const toggleOverlayOpen = useCallback(
		(assessmentResult: ClientSiteAssessmentResultsDTO) => {
			setIsModalOpen((prev) => !prev);
			setSelectedAssessmentResult(assessmentResult);
			setVitalsTestResultId(assessmentResult.vitalsTestResultId);
		},
		[setVitalsTestResultId]
	);

	const closeModal = useCallback(() => {
		setIsModalOpen(false);
		setSyncSrc(undefined);
		setDCATButtonClicked(false);
		setDCATRunning(false);
	}, [setSyncSrc, setDCATButtonClicked, setDCATRunning]);

	// ─── Custom field helpers ────────────────────────────────────────────────────
	const checkIfCustomFieldValueIsSuccess = useCallback(
		(validCustomFields: string[]): boolean => {
			return validCustomFields.every((v) => v === "success");
		},
		[]
	);

	const postAssessmentCustomFields = useCallback(() => {
		if (!selectedAssessmentResult) return;
		if (newTestCustomFieldValues && newTestCustomFieldValues.length > 0) {
			AssessmentResultsAPI.postAssessmentResultCustomFields({
				datasetId,
				assessmentResultId: selectedAssessmentResult.id,
				assessmentResultCustomFieldValues: newTestCustomFieldValues,
			});
		}
	}, [selectedAssessmentResult, newTestCustomFieldValues, datasetId]);

	const postAssessmentPurpose = useCallback(() => {
		if (!selectedAssessmentResult) return;
		if (
			selectedAssessmentResult.assessmentPurposeId &&
			selectedAssessmentResult.assessmentPurposeId > 0
		) {
			AssessmentResultsAPI.postAssessmentResultPurpose({
				datasetId,
				assessmentResultId: selectedAssessmentResult.id,
				assessmentPurposeId: selectedAssessmentResult.assessmentPurposeId,
			});
		}
	}, [selectedAssessmentResult, datasetId]);

	// ─── Navigation helpers ────────────────────────────────────────────────────────
	const getCoreTestResult = useCallback(async () => {
		const coreTestId = selectedAssessmentResult!.coreTestResultId;

		const coreEvaluators = await OrganizationAPI.getDatasetEvaluators({ tenantId: organizationId, datasetId });
		const selectedEvaluator = await OrganizationAPI.getEvaluator({
			tenantId: organizationId,
			datasetId,
			evaluatorId: selectedAssessmentResult!.coreAssessorId
				? selectedAssessmentResult!.coreAssessorId
				: 0,
		});
		if (
			selectedEvaluator.id === undefined &&
			coreEvaluators.length === 0 &&
			coreEvaluators.every((ce) => !ce.isActive!)
		) {
			toast.error("No active evaluator found, please contact administrator");
			return;
		}

		postAssessmentCustomFields();
		postAssessmentPurpose();

		history.push({
			pathname: `/organizations/${organizationId}/datasets/${datasetId}/clients/${clientId}/sessionId/${
				selectedAssessmentResult!.id
			}/core/${coreTestId}/general-appraisal`,
		});
	}, [
		selectedAssessmentResult,
		organizationId,
		datasetId,
		clientId,
		history,
		postAssessmentCustomFields,
		postAssessmentPurpose,
	]);

	const getVitalsTestResult = useCallback(() => {
		postAssessmentCustomFields();
		postAssessmentPurpose();

		history.push({
			pathname: `/organizations/${organizationId}/datasets/${datasetId}/clients/${clientId}/sessionId/${
				selectedAssessmentResult!.id
			}/vitalsTestResults/${vitalsTestResultId}`,
		});
	}, [
		selectedAssessmentResult,
		vitalsTestResultId,
		organizationId,
		datasetId,
		clientId,
		history,
		postAssessmentCustomFields,
		postAssessmentPurpose,
	]);

	const startCoreTest = useCallback(async () => {
		try {
			const res = await CoreAPI.checkActiveCoursesAndLocationsForCoreTest({
				organizationId,
				datasetId,
				clientId,
			});
			if (!res) {
				toast.error("No Active courses in Active Locations found, please contact administrator");
				return;
			}

			const coreEvaluators = await OrganizationAPI.getDatasetEvaluators({ tenantId: organizationId, datasetId });
			if (
				coreEvaluators.length === 0 &&
				coreEvaluators.every((ce) => !ce.isActive!)
			) {
				toast.error("No active CORE evaluators found, please contact administrator");
				return;
			}

			const coreUrl: string = location.pathname;
			let newCoreUrl = "";

			if (coreUrl.includes("/results")) {
				newCoreUrl = location.pathname.replace("/results", "");
			} else if (coreUrl.includes("/details")) {
				newCoreUrl = location.pathname.replace("/details", "");
			}

			const newTest = {
				datasetId: Number(datasetId),
				clientId: Number(clientId),
				coreGeneralSubmitted: false,
				dateAdministered: moment().format("MMM DD, YYYY"),
				id: undefined,
				dataLocked: true,
				coreTestCustomFieldValues: [],
				assessmentResultCustomFieldValues: newTestCustomFieldValues,
				startFromAssessmentId: selectedAssessmentResult ? selectedAssessmentResult.id : undefined,
				assessmentPurposeId: selectedAssessmentResult
					? selectedAssessmentResult.assessmentPurposeId
					: newTestPurposeId,
			} as UpsertCoreTestResultRequest;

			const coreTest = await CoreAPI.upsertCoreTest(newTest);
			const sessionId = coreTest.sessionId ? coreTest.sessionId : selectedAssessmentResult!.id;

			history.push(`${newCoreUrl}/sessionId/${sessionId}/core/${coreTest.id!}/general-appraisal`);
		} catch (error) {
			toast.error("Something went wrong");
		}
	}, [
		organizationId,
		datasetId,
		clientId,
		location,
		newTestCustomFieldValues,
		selectedAssessmentResult,
		newTestPurposeId,
		history,
	]);

	const startVitalsTest = useCallback(() => {
		startVitalsTestBase({
			datasetId,
			clientId,
			datasetLanguages: datasetLanguages || [],
			newTestCustomFieldValues,
			newTestPurposeId,
			selectedAssessmentResult,
		});
	}, [
		startVitalsTestBase,
		datasetId,
		clientId,
		datasetLanguages,
		newTestCustomFieldValues,
		newTestPurposeId,
		selectedAssessmentResult,
	]);

	const resumeVitalsTest = useCallback(() => {
		resumeVitalsTestBase({
			datasetId,
			datasetLanguages: datasetLanguages || [],
			vitalsTestResultId,
		});
	}, [resumeVitalsTestBase, datasetId, datasetLanguages, vitalsTestResultId]);

	const navigateToVitalsSummary = useCallback(
		(sessionId: number | undefined, careTestResultId: number | undefined) => {
			if (careTestResultId) {
				history.push(
					`/organizations/${organizationId}/datasets/${datasetId}/sessionid/${sessionId}/vitalsm/${careTestResultId}/productid/${dataset.productId}/summary`
				);
			}
		},
		[history, organizationId, datasetId, dataset]
	);

	const scoreAssessment = useCallback(
		async (assessmentId: number) => {
			try {
				const { scoringClient } = api;
				await scoringClient.scoreAssessment(assessmentId);
				toast.success("Score session request complete");
				loadData();
				setScoring(false);
			} catch {
				toast.error("Failed to score session");
			}
		},
		[api, loadData]
	);

	const onAssessmentCreated = useCallback(
		async (assessment: Assessment) => {
			const newAssessmentDto = Utils.mapClientSiteAssessmentResult(assessment);
			setSelectedAssessmentResult(newAssessmentDto);
			await loadData();
		},
		[loadData]
	);

	const onCustomFieldValuesUpdated = useCallback(async () => {
		await loadData();
	}, [loadData]);

	const getNewTestAssessmentCustomFieldValidation = useCallback(
		(validation: ICustomFieldValidation[]) => {
			const validCustomFieldValues = getCustomFieldValidation(validation);
			const statuses = validCustomFieldValues.map((c) => {
				if (c.validationStatus === undefined) return "";
				return c.validationStatus;
			});
			setValidNewTestValidation(statuses);
		},
		[]
	);

	const getAssessmentCustomFieldValues = useCallback(
		(customFieldValues: CustomFieldValueDTO[]) => {
			const values = getCustomFieldValues(customFieldValues);
			if (values.length > 0) {
				setNewTestCustomFieldValues(values);
			}
		},
		[]
	);

	const onNewTestClick = useCallback(() => {
		let promptForConfirmation = false;
		let incompleteAssessmentsCount = 0;

		if (
			hasAnyProduct(dataset.productId) &&
			(currentUser.vitalsCertified === true || currentUser.coreCertified === true)
		) {
			incompleteAssessmentsCount = results.filter(
				(x) =>
					(x.coreScoreCompleted !== true || x.vitalsScoreCompleted !== true) &&
					x.dateCompleted == undefined &&
					x.dataLocked == false
			).length;
			if (incompleteAssessmentsCount > 0) promptForConfirmation = true;
		} else if (hasAnyProduct(dataset.productId) && currentUser.coreCertified === true) {
			incompleteAssessmentsCount = results.filter(
				(x) => x.coreScoreCompleted !== true && x.dateCompleted == undefined
			).length;
			if (incompleteAssessmentsCount > 0) promptForConfirmation = true;
		} else if (hasAnyProduct(dataset.productId) && currentUser.vitalsCertified === true) {
			incompleteAssessmentsCount = results.filter(
				(x) => x.vitalsScoreCompleted !== true && x.dateCompleted == undefined
			).length;
			if (incompleteAssessmentsCount > 0) promptForConfirmation = true;
		}

		if (promptForConfirmation) {
			setIsNewTestConfirmationOpen(true);
		} else {
			setSelectedAssessmentResult(undefined);
			setIsModalOpen(true);
		}
	}, [dataset, currentUser, results]);

	// ─── getColumns ───────────────────────────────────────────────────────────────
	const getColumns = useCallback(
		(cols: CustomFieldDTO[]) => {
			const customColumns = CustomFields.renderCustomFieldColumn(cols, convertDisplayOnValuesToLabelComponent);

			const columns = [
				{
					Header: <Tooltip title="Web Id">Web Id</Tooltip>,
					accessor: "webId",
					filterable: true,
				},
				{
					Header: <Tooltip title="Date Created">Date Created</Tooltip>,
					accessor: "dateCreated",
					Cell: setDateColumnCellValue,
					filterMethod: dateFilterMethod("dateCreated"),
					Filter: ({ onChange }: any) => (
						<DateFilter
							accessor="dateCreated"
							onDateChange={onDateChange}
							date={filterState.dateCreated}
							onChange={onChange}
						/>
					),
				},
				{
					Header: overallScoreToolTip(dataset.productId),
					show: hasAnyProduct(dataset.productId),
					accessor: "excelerateScore",
					Cell: (rowInfo: any) => {
						if (rowInfo.row.excelerateScore) {
							return rowInfo.row.excelerateScore;
						} else if (
							(rowInfo.original.coreScoreCompleted && rowInfo.original.vitalsScoreCompleted) ||
							(rowInfo.original.coreScoreCompleted && rowInfo.original.careScoreCompleted)
						) {
							return "C";
						} else {
							return "NC";
						}
					},
				},
				{
					Header: inOfficeScoreToolTip(dataset.productId),
					show: !hasExceleRATEV1OnlyProduct(dataset.productId),
					accessor: "careScore",
					Cell: (rowInfo: any) =>
						getCareScore(
							rowInfo.row.careScore,
							rowInfo.original.careScoreCompleted,
							rowInfo.original.careTestResultId
						),
				},
				{
					Header: inofficeRecommendationToolTip(dataset.productId),
					show: !hasExceleRATEV1OnlyProduct(dataset.productId),
					accessor: "careRecommendation",
					Cell: (rowInfo: any) => (
						<InofficeRecommendationCell result={rowInfo.original} />
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<SwitchFilter
							closeDisplayFilter={closeFilter}
							convertForFilter={setFilterValue}
							displayFilterName="displayInofficeRecommendationFilters"
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							shouldDisplayFilter={
								filterState.displayFilters.displayInofficeRecommendationFilters &&
								filterState.showFilters
							}
							onChange={onChange}
							name="inofficeRecommendationStatus"
							values={filterState.inofficeRecommendationStatus}
						/>
					),
					filterMethod: (filter: any, row: any) => setFilterMethodForSwitch(filter, row),
				},
				{
					Header: <Tooltip title="VITALS">VITALS</Tooltip>,
					show: hasExceleRATEV1OnlyProduct(dataset.productId),
					accessor: "vitalsScore",
					Cell: (rowInfo: any) => (
						<VitalsScoreCell result={rowInfo.original} canSeeScores={userCanSeeScores} />
					),
				},
				{
					Header: (
						<Tooltip title="VITALS Recommendation">VITALS RECOMMENDATION</Tooltip>
					),
					show: hasExceleRATEV1OnlyProduct(dataset.productId),
					accessor: "vitalsStatusId",
					Cell: (rowInfo: any) => (
						<VitalsRecommendationCell result={rowInfo.original} />
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<SwitchFilter
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							closeDisplayFilter={closeFilter}
							displayFilterName="displayVitalsFilters"
							shouldDisplayFilter={filterState.displayFilters.displayVitalsFilters}
							convertForFilter={setFilterValue}
							onChange={onChange}
							name="vitalsStatus"
							values={filterState.vitalsStatus}
						/>
					),
					filterMethod: (filter: any, row: any) => setFilterMethodForSwitch(filter, row),
				},
				{
					Header: (
						<Tooltip title="C-Error">
							<span style={{ whiteSpace: "nowrap" }}>C-Error</span>
						</Tooltip>
					),
					show: hasAnyExceleRATEProduct(dataset.productId),
					accessor: "cErrorStatusId",
					Cell: (rowInfo: any) => (
						<CoreRecommendationCell
							dateCompleted={rowInfo.original.coreScoreCompleted}
							testId={rowInfo.original.coreTestResultId}
							recommendation={rowInfo.row.cErrorStatusId}
						/>
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<SwitchFilter
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							closeDisplayFilter={closeFilter}
							displayFilterName="displayCerrorFilters"
							shouldDisplayFilter={
								filterState.displayFilters.displayCerrorFilters && filterState.showFilters
							}
							convertForFilter={setFilterValue}
							onChange={onChange}
							name="cErrorStatus"
							values={filterState.cErrorStatus}
						/>
					),
					filterMethod: (filter: any, row: any) => setFilterMethodForSwitch(filter, row),
				},
				{
					Header: (
						<Tooltip title="T-Error">
							<span style={{ whiteSpace: "nowrap" }}>T-Error</span>
						</Tooltip>
					),
					show: hasAnyExceleRATEProduct(dataset.productId),
					accessor: "tErrorStatusId",
					Cell: (rowInfo: any) => (
						<CoreRecommendationCell
							dateCompleted={rowInfo.original.coreScoreCompleted}
							testId={rowInfo.original.coreTestResultId}
							recommendation={rowInfo.row.tErrorStatusId}
						/>
					),
					Filter: ({ onChange }: { onChange: any }) => {
						const switchFilter = (
							<SwitchFilter
								convertForFilter={setFilterValue}
								closeDisplayFilter={closeFilter}
								showOrHideDisplayFilters={showOrHideDisplayFilter}
								displayFilterName="displayTerrorFilters"
								shouldDisplayFilter={
									filterState.displayFilters.displayTerrorFilters && filterState.showFilters
								}
								onChange={onChange}
								name="tErrorStatus"
								values={filterState.tErrorStatus}
							/>
						);
						return (
							<Popover trigger="click" arrowPointAtCenter={true}>
								{switchFilter}
							</Popover>
						);
					},
					filterMethod: (filter: any, row: any) => setFilterMethodForSwitch(filter, row),
				},
				{
					Header: <Tooltip title="Skills Appraisal">Skills Appraisal</Tooltip>,
					show: hasAnyExceleRATEProduct(dataset.productId),
					accessor: "appraisalStatusId",
					Cell: (rowInfo: any) => (
						<CoreRecommendationCell
							dateCompleted={rowInfo.original.coreScoreCompleted}
							testId={rowInfo.original.coreTestResultId}
							recommendation={rowInfo.row.appraisalStatusId}
						/>
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<SwitchFilter
							closeDisplayFilter={closeFilter}
							convertForFilter={setFilterValue}
							displayFilterName="displayAppraisalFilters"
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							shouldDisplayFilter={
								filterState.displayFilters.displayAppraisalFilters && filterState.showFilters
							}
							onChange={onChange}
							name="appraisalStatus"
							values={filterState.appraisalStatus}
						/>
					),
					filterMethod: (filter: any, row: any) => setFilterMethodForSwitch(filter, row),
				},
				{
					Header: <Tooltip title="Neurapulse Result">Neurapulse Result</Tooltip>,
					show: dataset.neurapulseProjectId !== undefined,
					accessor: "neurapulseState",
					filterable: true,
					Cell: (rowInfo: any) => (
						<NeurapulseResultCell neurapulseStatusId={rowInfo.row.neurapulseState} />
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<NeurapulseFilter
							closeDisplayFilter={closeFilter}
							convertForFilter={setNeurapulseFilterValue}
							displayFilterName="displayNeurapulseFilters"
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							shouldDisplayFilter={
								filterState.displayFilters.displayNeurapulseFilters && filterState.showFilters
							}
							onChange={onChange}
							name="neurapulseStatus"
							values={filterState.neurapulseStatus}
						/>
					),
					filterMethod: (filter: any, row: any) =>
						setFilterMethodForNeurapulseSwitch(filter, row),
				},
				{
					Header: (
						<Tooltip title="Neurapulse Completed">Neurapulse Completed</Tooltip>
					),
					show: dataset.neurapulseProjectId !== undefined,
					accessor: "neurapulseCompleted",
					Cell: setDateColumnCellValue,
					filterMethod: dateFilterMethod("neurapulseCompleted"),
					Filter: ({ onChange }: any) => (
						<DateFilter
							accessor="neurapulseCompleted"
							onDateChange={onDateChange}
							date={filterState.neurapulseCompleted}
							onChange={onChange}
						/>
					),
					filterable: true,
				},
				{
					Header: onroadScoreToolTip(dataset.productId),
					show: hasAnyProduct(dataset.productId),
					accessor: "coreScore",
					Cell: (rowInfo: any) => {
						if (rowInfo.row.coreScore) {
							return rowInfo.row.coreScore;
						} else if (rowInfo.original.coreScoreCompleted) {
							return "C";
						} else if (!rowInfo.original.coreTestResultId) {
							return "-";
						} else {
							return "NC";
						}
					},
				},
				{
					Header: (
						<Tooltip title="DORE Recommendation">DORE RECOMMENDATION</Tooltip>
					),
					show: hasDriveABLEProduct(dataset.productId),
					accessor: "onroadRecommendation",
					Cell: (rowInfo: any) => (
						<DoreRecommendationCell
							dateCompleted={rowInfo.original.coreScoreCompleted}
							testId={rowInfo.original.coreTestResultId}
							recommendation={rowInfo.row.onroadRecommendation}
						/>
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<SwitchFilter
							closeDisplayFilter={closeFilter}
							convertForFilter={setFilterValue}
							displayFilterName="displayOnroadRecommendationFilters"
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							shouldDisplayFilter={
								filterState.displayFilters.displayOnroadRecommendationFilters &&
								filterState.showFilters
							}
							onChange={onChange}
							name="onroadRecommendationStatus"
							values={filterState.onroadRecommendationStatus}
						/>
					),
					filterMethod: (filter: any, row: any) => setFilterMethodForSwitch(filter, row),
				},
				{
					Header: <Tooltip title="Vehicle">Vehicle</Tooltip>,
					show: hasAnyProduct(dataset.productId),
					accessor: "vehicleType",
				},
				{
					Header: <Tooltip title="Course">Course</Tooltip>,
					show: hasAnyProduct(dataset.productId),
					accessor: "courseName",
					Cell: (rowInfo: any) => (
						<React.Fragment>{rowInfo.value}</React.Fragment>
					),
				},
				{
					Header: (
						<Tooltip title="Commercial Driving Experience">
							Commercial Driving Experience
						</Tooltip>
					),
					id: "monthsCommercialDrivingExp",
					accessor: "monthsCommercialDrivingExp",
					show:
						hasAnyExceleRATEProduct(dataset.productId) &&
						commercialDrivingExpSettings.showCommDrivingExp,
					Cell: (rowInfo: any) => {
						if (rowInfo.value !== undefined) {
							return <>{Utils.getCommercialDrivingExpDesc(rowInfo.value)}</>;
						} else {
							return <></>;
						}
					},
					filterable: true,
				},
				{
					Header: <Tooltip title="Trip Inspection">Trip Inspection</Tooltip>,
					id: "hasTripInspection",
					accessor: "hasTripInspection",
					show: hasAnyExceleRATEProduct(dataset.productId),
					Cell: (rowInfo: any) => {
						if (rowInfo.value === true) {
							return <div>True</div>;
						} else {
							return <div>False</div>;
						}
					},
					filterable: true,
				},
			];

			const actionColumn = {
				Header: <Tooltip title="Actions">Actions</Tooltip>,
				id: "id",
				accessor: "id",
				Filter: () => (
					<Button className="btn-outline-primary" onClick={clearAll}>
						Clear
					</Button>
				),
				Cell: (row: any) => (
					<React.Fragment>
						<IconButton
							className="p-1 m-0.5 btn-link"
							title="Edit Session"
							onClick={() => toggleOverlayOpen(row.original)}
							disabled={!enableEditAssessment(row.original)}
						>
							<Icon icon={SquarePen} />
						</IconButton>
						{row.original.allowToAccessReport && (
							<GenerateExceleRATEPdf
								fetchReportData={() =>
									fetchReportData({
										reportId: row.original.id,
										existingReportId: row.original.reportId,
										dataLocked: row.original.dataLocked,
										datasetProductId: dataset.productId,
										datasetId,
										organizationId,
										currentUser,
										commercialDrivingExpSettings,
										showEmployeeId: dataset.showClientParticipantId,
									})
								}
							/>
						)}
						{row.original.dataLocked && (
							<IconButton className="p-1 m-0.5 btn-link" title="locked">
								<Icon icon={Lock} />
							</IconButton>
						)}
						{row.original.dataLocked === false &&
							currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) && (
								<IconButton
									className="p-1 m-0.5 btn-link"
									title="Score Session"
									onClick={() => {
										setScoring(true);
										scoreAssessment(row.original.id);
									}}
									disabled={scoring}
								>
									<Icon icon={ClipboardCheck} />
								</IconButton>
							)}
					</React.Fragment>
				),
			};

			return [...columns, ...customColumns, actionColumn];
		},
		[
			dataset,
			commercialDrivingExpSettings,
			filterState,
			userCanSeeScores,
			clearAll,
			toggleOverlayOpen,
			enableEditAssessment,
			fetchReportData,
			datasetId,
			organizationId,
			currentUser,
			scoring,
			scoreAssessment,
			closeFilter,
			setFilterValue,
			showOrHideDisplayFilter,
			setFilterMethodForSwitch,
			setNeurapulseFilterValue,
			setFilterMethodForNeurapulseSwitch,
			onDateChange,
			convertDisplayOnValuesToLabelComponent,
		]
	);

	// ─── Render ────────────────────────────────────────────────────────────────────
	const columns = getColumns(customFields);

	const timestamp = getTime("date");
	const fullname = `${client.firstName} ${client.lastName}`;
	const csvExportFilename = `${fullname} Results - ${timestamp}.csv`;
	const hasResults = csvTestResults && csvTestResults.csvResults.length > 0;
	const hasFilteredResults = csvTestResultsFiltered && csvTestResultsFiltered.csvResults.length > 0;
	const showExportButton = hasResults || hasFilteredResults;
	const dp = getDatasetPermissions(currentUser, datasetId);

	if (context.pickListItems.length > 0 && isLoading !== false)
		return (
			<p>
				<Spinner />
			</p>
		);

	if (viewResults === false) {
		return <span>You do not have permission to view results</span>;
	}

	return (
		<div className="container-fluid">
			<div className="d-flex">
				<div>
					<h2 className="mt-2">{dataset.name} Test Results</h2>
				</div>
				<div className="my-3">
					<div className="ml-2">C = Completed, NC = Not Completed</div>
				</div>
				<div className="ml-auto pr-2">
					{showExportButton ? (
						<Dropdown>
							<Dropdown.Toggle variant="primary" id="dropdown-split-basic">
								Export Results &nbsp;
								<Icon icon={FileSpreadsheet} />
							</Dropdown.Toggle>
							<Dropdown.Menu>
								{hasResults && (
									<CSVLink
										className="btn"
										data={csvTestResults.csvResults}
										headers={csvTestResults.headers}
										filename={csvExportFilename}
									>
										Export All Results
									</CSVLink>
								)}
								{hasFilteredResults && (
									<CSVLink
										className="btn"
										data={csvTestResultsFiltered.csvResults}
										headers={csvTestResultsFiltered.headers}
										filename={csvExportFilename}
									>
										Export Filtered Results
									</CSVLink>
								)}
							</Dropdown.Menu>
						</Dropdown>
					) : null}
				</div>

				{enableStartAssessment() ? (
					<div>
						<Button
							className="btn-primary"
							onClick={onNewTestClick}
							disabled={!enableStartAssessment()}
						>
							New Test
						</Button>
					</div>
				) : null}
			</div>
			<DataTable
				data={results}
				columns={columns}
				filterable={true}
				defaultSorted={[
					{
						id: "dateCreated",
						desc: true,
					},
				]}
				filtered={filtered}
				onFilteredChange={(f: any[]) => {
					setFiltered(f);
				}}
				noDataText="No test results."
				loading={isLoading}
				ref={reactTable}
				onDataFiltered={onDataFiltered}
				storageKey={clientResultsGridStorageKey + datasetId}
			/>
			{syncSrc ? (
				<CustomProtocolHandler
					src={syncSrc}
					onerror={handleCustomProtocolError}
				/>
			) : null}
			<ClientResultModalOverlay
				vitalsDateCompleted={
					selectedAssessmentResult
						? selectedAssessmentResult.vitalsDateCompleted
						: undefined
				}
				withAssessment={selectedAssessmentResult ? true : false}
				closeModal={closeModal}
				modalOpen={isModalOpen}
				existingAssessmentResultCloseModal={closeModal}
				customFieldNotValidated={checkIfCustomFieldValueIsSuccess(validNewTestValidation)}
				selectedPurposeId={
					selectedAssessmentResult
						? selectedAssessmentResult.assessmentPurposeId
						: newTestPurposeId
				}
				onSelectedPurposeChange={(v: number) => {
					if (selectedAssessmentResult) {
						selectedAssessmentResult.assessmentPurposeId = v;
						setSelectedAssessmentResult({ ...selectedAssessmentResult });
					} else {
						setNewTestPurposeId(v);
					}
				}}
				coreButtonProps={{
					licensedProductContainsCore: hasAnyProduct(dataset.productId),
					dataLocked:
						selectedAssessmentResult &&
						selectedAssessmentResult.dataLocked !== undefined
							? selectedAssessmentResult.dataLocked
							: false,
					coreTestResultId:
						selectedAssessmentResult &&
						selectedAssessmentResult.coreTestResultId !== undefined
							? selectedAssessmentResult.coreTestResultId
							: undefined,
					getCoreTest: getCoreTestResult,
					startCore: startCoreTest,
					canEditCore:
						currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
						datasetModules.find(
							(m) => m.id === ModuleType.CORE || m.id === ModuleType.DORE
						),
				}}
				vitalButtonProps={{
					licensedProductContainsVitals: hasExceleRATEV1OnlyProduct(dataset.productId),
					dataLocked:
						selectedAssessmentResult &&
						selectedAssessmentResult.dataLocked !== undefined
							? selectedAssessmentResult.dataLocked
							: false,
					vitalsId:
						selectedAssessmentResult &&
						selectedAssessmentResult.vitalsTestResultId !== undefined
							? selectedAssessmentResult.vitalsTestResultId
							: undefined,
					vitalsScore:
						selectedAssessmentResult &&
						selectedAssessmentResult.vitalsScore !== undefined
							? selectedAssessmentResult.vitalsScore
							: undefined,
					DCATButtonClicked: DCATButtonClicked,
					DCATRunning: DCATRunning,
					getVitalsTest: getVitalsTestResult,
					resumeVitalsTest: resumeVitalsTest,
					startVitalsTest: startVitalsTest,
					vitalsLaunched: vitalsLaunched,
					showLaunchedMessageHandle: showLaunchMessageVisible,
					canSync: canSync,
					canEditVitals:
						currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
						(dp &&
							dp.permissions &&
							dp.permissions.includes(ClientPermissions.EditVitalsTest) &&
							currentUser.vitalsCertified),
				}}
				vitalsSummaryProps={{
					careTestResultId:
						selectedAssessmentResult && selectedAssessmentResult !== undefined
							? selectedAssessmentResult.careTestResultId
							: undefined,
					dataLocked:
						selectedAssessmentResult &&
						selectedAssessmentResult.dataLocked !== undefined
							? selectedAssessmentResult.dataLocked
							: false,
					canEditCare:
						currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
						datasetModules.find(
							(m) => m.id === ModuleType.VITALS || m.id === ModuleType.DCAT
						),
					getVitalsSummary: () => {
						if (selectedAssessmentResult && selectedAssessmentResult !== undefined) {
							navigateToVitalsSummary(
								selectedAssessmentResult.id,
								selectedAssessmentResult.careTestResultId
							);
						}
					},
				}}
				customFieldProps={{
					datasetId: +datasetId,
					organizationId: +organizationId,
					pickListItems: context.pickListItems,
					customFieldTypes: ["AssessmentType"],
					customFieldTypeIds:
						selectedAssessmentResult !== undefined
							? {
									typeOfCustomFieldValue: "AssessmentType",
									value: selectedAssessmentResult.id!,
							  }
							: undefined,
					getCustomFieldValidation: getNewTestAssessmentCustomFieldValidation,
					getCustomFieldValueFromState: getAssessmentCustomFieldValues,
				}}
				assessmentProps={{
					assessmentResultId:
						selectedAssessmentResult !== undefined
							? selectedAssessmentResult.id
								? selectedAssessmentResult.id
								: 0
							: 0,
					organizationId: organizationId,
					datasetId: datasetId,
					clientId: client.id ? client.id : 0,
					neurapulseEnabled: dataset.neurapulseProjectId !== undefined,
					hasNeurapulseResult:
						selectedAssessmentResult !== undefined &&
						selectedAssessmentResult.neurapulseCompleted
							? true
							: false,
					onAssessmentCreated: onAssessmentCreated,
					onCustomFieldValuesUpdated: onCustomFieldValuesUpdated,
				}}
				productType={dataset.productId}
			/>
			<ModalOverlay
				isOpen={isNewTestConfirmationOpen}
				modalSize="modal-sm"
				headerChildren="New Test Confirmation"
				children="One or more sessions are incomplete. Are you sure you want to create a new test?"
				confirmButtonChildren="Continue"
				confirmButtonAction={() => {
					setIsNewTestConfirmationOpen(false);
					setSelectedAssessmentResult(undefined);
					setIsModalOpen(true);
				}}
				cancelButtonChildren="Cancel"
				cancelButtonAction={() => {
					setIsNewTestConfirmationOpen(false);
				}}
				onRequestClose={() => {
					setIsNewTestConfirmationOpen(false);
				}}
			/>
		</div>
	);
};

export default ClientResultsTable;
